import {
  useEffect,
  useReducer,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
} from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { Textarea } from "~/components/ui/textarea";
import {
  AdminApiError,
  downloadShippingList,
  fetchPendingShipments,
  importShipments,
  recordShipmentAcceptance,
  registerShipment,
} from "~/lib/admin-api";
import {
  dateDisplayProps,
  formatDate,
  formatDateTime,
  formatMillingType,
  formatVariety,
  shortId,
} from "~/lib/format";
import {
  parseShipmentCsv,
  ShipmentCsvError,
} from "~/lib/shipment-csv";
import {
  canCommitShipmentImport,
  createShipmentHandoverDraft,
  initialShipmentImportState,
  shipmentImportReducer,
  unregisteredShipmentReferenceLabel,
} from "~/lib/shipment-import-state";
import type {
  ShipmentHandover,
  ShipmentImportError,
  ShipmentImportResult,
  ShipmentImportRow,
  ShipmentTargetKind,
} from "~/types/admin-shipping";

const permissionErrorMessage =
  "管理者権限がありません。権限が変更された可能性があります — 再ログインしてください。";

const importReasonLabels: Readonly<Record<string, string>> = {
  invalid_tracking_number: "追跡番号が12桁の数字ではありません",
  target_not_found_or_not_closed: "対象が存在しないか、まだ締切済みではありません",
  target_not_shippable: "キャンセル・返金・災害停止などにより出荷できません",
  customer_email_missing: "発送通知先のメールアドレスがありません",
  duplicate_tracking_number_in_file: "CSV 内で追跡番号が重複しています",
  duplicate_target_in_file: "CSV 内で同じ対象が重複しています",
  tracking_number_already_used: "追跡番号が別の対象に登録済みです",
  target_already_has_different_tracking_number:
    "対象に別の追跡番号が登録済みです",
};

function shouldRetry(failureCount: number, error: Error) {
  if (
    error instanceof AdminApiError &&
    error.status >= 400 &&
    error.status < 500
  ) {
    return false;
  }
  return failureCount < 2;
}

function isPermissionError(error: Error | null): boolean {
  return (
    error instanceof AdminApiError &&
    (error.status === 401 ||
      error.status === 403 ||
      error.code === "FORBIDDEN" ||
      error.code === "UNAUTHORIZED")
  );
}

function mutationErrorMessage(error: Error | null): string | null {
  if (!error) return null;
  if (isPermissionError(error)) return permissionErrorMessage;
  if (error instanceof AdminApiError) {
    switch (error.code) {
      case "invalid_handover":
        return "郵便引渡し情報を確認してください。";
      case "invalid_acceptance":
        return "引受日と選択した発送を確認してください。";
      case "shipment_not_found":
        return "対象の発送が見つかりません。画面を更新してください。";
      case "cycle_state_conflict":
        return "プレビュー後に対象の状態が変わりました。CSV を再度プレビューしてください。";
    }
    return error.message;
  }
  return "処理に失敗しました。しばらくしてからもう一度お試しください。";
}

function todayInJapan(): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((value) => value.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}`;
}

function targetKindLabel(kind: ShipmentTargetKind): string {
  return kind === "cycle" ? "定期の回" : "単品便";
}

function importReason(reason: string): string {
  return importReasonLabels[reason] ?? reason;
}

function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function csvCell(value: string | number): string {
  const text = String(value);
  return /[",\r\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}

function downloadImportErrors(errors: readonly ShipmentImportError[]) {
  const rows = [
    ["row_number", "reason", "message"],
    ...errors.map((error) => [
      error.row_number,
      error.reason,
      importReason(error.reason),
    ]),
  ];
  const csv = `\uFEFF${rows
    .map((row) => row.map(csvCell).join(","))
    .join("\r\n")}\r\n`;
  downloadBlob(
    new Blob([csv], { type: "text/csv;charset=utf-8" }),
    "shipment-import-errors.csv",
  );
}

function EmptyRow({ columns, label }: { columns: number; label: string }) {
  return (
    <TableRow>
      <TableCell
        colSpan={columns}
        className="h-20 text-center text-muted-foreground"
      >
        {label}
      </TableCell>
    </TableRow>
  );
}

function PreviewCount({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border bg-muted/30 p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-xl font-semibold tabular-nums">{value}</p>
    </div>
  );
}

export function Shipping({ accessToken }: { accessToken: string }) {
  const today = todayInJapan();
  const [windowStart, setWindowStart] = useState(today);
  const [csvError, setCsvError] = useState<string | null>(null);
  const [importState, dispatchImport] = useReducer(
    shipmentImportReducer,
    initialShipmentImportState,
  );
  const latestBatchId = useRef<string | null>(null);
  const [recordHandover, setRecordHandover] = useState(true);
  const [handedOverOn, setHandedOverOn] = useState(today);
  const [handoverMethod, setHandoverMethod] =
    useState<ShipmentHandover["method"]>("counter");
  const [reportedCount, setReportedCount] = useState("");
  const [handoverNote, setHandoverNote] = useState("");

  const [manualKind, setManualKind] =
    useState<ShipmentTargetKind>("cycle");
  const [manualTargetId, setManualTargetId] = useState("");
  const [manualTrackingNumber, setManualTrackingNumber] = useState("");
  const [manualConfirmOpen, setManualConfirmOpen] = useState(false);
  const [manualResult, setManualResult] =
    useState<ShipmentImportResult | null>(null);

  const [selectedShipmentIds, setSelectedShipmentIds] = useState<string[]>([]);
  const [acceptedOn, setAcceptedOn] = useState(today);

  const pendingQuery = useQuery({
    queryKey: ["pending-shipments"],
    queryFn: ({ signal }) => fetchPendingShipments(accessToken, signal),
    refetchInterval: 30_000,
    retry: shouldRetry,
  });

  const downloadMutation = useMutation({
    mutationFn: (date: string) => downloadShippingList(accessToken, date),
    onSuccess: (blob, date) => {
      downloadBlob(blob, `shipping-list-${date}.csv`);
      toast.success("出荷リストをダウンロードしました。");
    },
  });

  const previewMutation = useMutation({
    mutationFn: (batch: {
      batchId: string;
      fileName: string;
      rows: ShipmentImportRow[];
      rowCount: number;
    }) =>
      importShipments(accessToken, {
        rows: batch.rows,
        dry_run: true,
        handover: null,
      }),
    onSuccess: (preview, batch) => {
      dispatchImport({
        type: "preview_received",
        batchId: batch.batchId,
        preview,
      });
    },
    onError: (_error, batch) => dispatchImport({
      type: "preview_failed",
      batchId: batch.batchId,
    }),
  });

  const commitMutation = useMutation({
    mutationFn: ({
      rows,
      handover,
    }: {
      rows: ShipmentImportRow[];
      handover: ShipmentHandover | null;
    }) =>
      importShipments(accessToken, {
        rows,
        dry_run: false,
        handover,
      }),
    onMutate: () => dispatchImport({ type: "commit_started" }),
    onSuccess: async (result) => {
      dispatchImport({ type: "commit_received", result });
      if (result.result === "committed") {
        await pendingQuery.refetch();
        toast.success(
          `追跡番号を ${result.registered_count} 件登録しました。`,
        );
      }
    },
    onError: () => dispatchImport({ type: "commit_failed" }),
  });

  const manualMutation = useMutation({
    mutationFn: (row: ShipmentImportRow) => registerShipment(accessToken, row),
    onSuccess: async (result) => {
      setManualResult(result);
      setManualConfirmOpen(false);
      if (result.result === "committed") {
        setManualTargetId("");
        setManualTrackingNumber("");
        await pendingQuery.refetch();
        toast.success(
          result.registered_count > 0
            ? "追跡番号を登録しました。"
            : "同じ追跡番号が登録済みのためスキップしました。",
        );
      }
    },
  });

  const acceptanceMutation = useMutation({
    mutationFn: () =>
      recordShipmentAcceptance(accessToken, {
        shipment_ids: selectedShipmentIds,
        accepted_on: acceptedOn,
      }),
    onSuccess: async (result) => {
      setSelectedShipmentIds([]);
      await pendingQuery.refetch();
      toast.success(
        `引受を ${result.recorded_count} 件記録しました${
          result.noop_count > 0 ? `（記録済み ${result.noop_count} 件）` : ""
        }。`,
      );
    },
  });

  useEffect(() => {
    const available = new Set(
      pendingQuery.data?.unaccepted.map((shipment) => shipment.shipment_id) ??
        [],
    );
    setSelectedShipmentIds((current) =>
      current.filter((shipmentId) => available.has(shipmentId)),
    );
  }, [pendingQuery.data]);

  async function handleCsvChange(event: ChangeEvent<HTMLInputElement>) {
    const input = event.currentTarget;
    const file = input.files?.[0];
    if (!file) return;
    const batchId = crypto.randomUUID();
    latestBatchId.current = batchId;

    setCsvError(null);
    previewMutation.reset();
    commitMutation.reset();
    dispatchImport({ type: "reset" });

    try {
      const parsed = parseShipmentCsv(await file.text());
      if (latestBatchId.current !== batchId) return;
      const batch = {
        batchId,
        fileName: file.name,
        rows: parsed.rows,
        rowCount: parsed.rowCount,
      };
      const handoverDraft = createShipmentHandoverDraft(
        batch.rowCount,
        today,
      );
      setRecordHandover(handoverDraft.recordHandover);
      setHandedOverOn(handoverDraft.handedOverOn);
      setHandoverMethod(handoverDraft.method);
      setReportedCount(handoverDraft.reportedCount);
      setHandoverNote(handoverDraft.note);
      dispatchImport({ type: "file_parsed", batch });
      previewMutation.mutate(batch);
    } catch (error) {
      if (latestBatchId.current !== batchId) return;
      if (error instanceof ShipmentCsvError) {
        setCsvError(
          error.rowNumber === undefined
            ? error.message
            : `${error.rowNumber} 行目: ${error.message}`,
        );
      } else {
        setCsvError("CSV ファイルを読み取れませんでした。");
      }
    } finally {
      input.value = "";
    }
  }

  function handleDownload(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!windowStart || downloadMutation.isPending) return;
    downloadMutation.mutate(windowStart);
  }

  function handleCommit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canCommitShipmentImport(importState) || commitMutation.isPending) {
      return;
    }

    let handover: ShipmentHandover | null = null;
    if (recordHandover) {
      const count = reportedCount.trim();
      handover = {
        handed_over_on: handedOverOn,
        method: handoverMethod,
        ...(count.length === 0 ? {} : { reported_count: Number(count) }),
        ...(handoverNote.trim().length === 0
          ? {}
          : { note: handoverNote.trim() }),
      };
    }

    commitMutation.mutate({ rows: importState.batch.rows, handover });
  }

  function handleManualReview(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!manualTargetId.trim() || !manualTrackingNumber.trim()) return;
    manualMutation.reset();
    setManualResult(null);
    setManualConfirmOpen(true);
  }

  function handleManualCommit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (manualMutation.isPending) return;
    manualMutation.mutate({
      target_kind: manualKind,
      target_id: manualTargetId.trim(),
      tracking_number: manualTrackingNumber.trim(),
    });
  }

  function toggleShipment(shipmentId: string, checked: boolean) {
    setSelectedShipmentIds((current) =>
      checked
        ? [...new Set([...current, shipmentId])]
        : current.filter((id) => id !== shipmentId),
    );
  }

  function toggleAllShipments(checked: boolean) {
    setSelectedShipmentIds(
      checked
        ? (pendingQuery.data?.unaccepted.map(
            (shipment) => shipment.shipment_id,
          ) ?? [])
        : [],
    );
  }

  const preview =
    importState.phase === "ready" ||
    importState.phase === "committing" ||
    importState.phase === "complete"
      ? importState.preview
      : null;
  const batch = importState.phase === "idle" ? null : importState.batch;
  const unregisteredCount = pendingQuery.data?.unregistered.length;
  const handoverValid =
    !recordHandover ||
    (handedOverOn.length > 0 &&
      reportedCount.trim().length > 0 &&
      Number.isInteger(Number(reportedCount)) &&
      Number(reportedCount) >= 0);
  const allUnacceptedSelected =
    (pendingQuery.data?.unaccepted.length ?? 0) > 0 &&
    selectedShipmentIds.length === pendingQuery.data?.unaccepted.length;
  const downloadError = mutationErrorMessage(downloadMutation.error);
  const previewError = mutationErrorMessage(previewMutation.error);
  const commitError = mutationErrorMessage(commitMutation.error);
  const manualError = mutationErrorMessage(manualMutation.error);
  const acceptanceError = mutationErrorMessage(acceptanceMutation.error);

  if (pendingQuery.error && isPermissionError(pendingQuery.error)) {
    return (
      <Alert variant="destructive" className="mx-auto max-w-2xl">
        <AlertTitle>権限がありません</AlertTitle>
        <AlertDescription>{permissionErrorMessage}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="grid gap-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">出荷</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            出荷リスト作成から追跡番号登録、翌営業日の引受記録までを行います。
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void pendingQuery.refetch()}
          disabled={pendingQuery.isFetching}
        >
          {pendingQuery.isFetching ? "更新中…" : "未処理一覧を更新"}
        </Button>
      </div>

      {pendingQuery.error ? (
        <Alert variant="destructive">
          <AlertTitle>未処理一覧を取得できませんでした</AlertTitle>
          <AlertDescription>
            {mutationErrorMessage(pendingQuery.error)}
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="grid gap-6 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>1. 出荷リストを取り出す</CardTitle>
            <CardDescription>
              お届け期間の開始日を選び、追跡番号記入用 CSV を取得します。
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form className="flex flex-wrap items-end gap-3" onSubmit={handleDownload}>
              <div className="grid min-w-56 gap-2">
                <Label htmlFor="shipping-window-start">お届け期間の開始日</Label>
                <Input
                  id="shipping-window-start"
                  type="date"
                  required
                  value={windowStart}
                  onChange={(event) => {
                    setWindowStart(event.target.value);
                    downloadMutation.reset();
                  }}
                />
              </div>
              <Button type="submit" disabled={downloadMutation.isPending}>
                {downloadMutation.isPending ? "作成中…" : "CSV をダウンロード"}
              </Button>
            </form>
            {downloadError ? (
              <Alert variant="destructive" className="mt-4">
                <AlertDescription>{downloadError}</AlertDescription>
              </Alert>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>運用上の注意</CardTitle>
            <CardDescription>CSV 取込は郵便引渡し後・当日中に確定します。</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 text-sm text-muted-foreground">
            <p>CSV は Excel で開かず、追跡番号列を文字列のまま編集してください。</p>
            <p>郵便引渡しは窓口持込または集荷とし、ポスト投函はしません。</p>
            <p>封筒と CSV 行の取り違えがないことを、取込前に確認してください。</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>2. CSV で追跡番号を登録する</CardTitle>
          <CardDescription>
            ファイル選択後に自動で dry-run します。エラー0件のプレビューだけ確定できます。
          </CardDescription>
          {batch ? (
            <CardAction>
              <Badge variant="outline">{batch.fileName}</Badge>
            </CardAction>
          ) : null}
        </CardHeader>
        <CardContent className="grid gap-6">
          <div className="grid gap-2">
            <Label htmlFor="shipment-csv">番号記入済み CSV</Label>
            <Input
              id="shipment-csv"
              type="file"
              accept=".csv,text/csv"
              onChange={(event) => void handleCsvChange(event)}
              disabled={previewMutation.isPending || commitMutation.isPending}
            />
            <p className="text-xs text-muted-foreground">
              ヘッダ行は除外し、target_kind・target_id・tracking_number 列を列名で読み取ります。
            </p>
          </div>

          {csvError || previewError ? (
            <Alert variant="destructive">
              <AlertTitle>CSV をプレビューできません</AlertTitle>
              <AlertDescription>{csvError ?? previewError}</AlertDescription>
            </Alert>
          ) : null}

          {importState.phase === "previewing" ? (
            <p className="text-sm text-muted-foreground">内容を検証しています…</p>
          ) : null}

          {preview && batch ? (
            <form className="grid gap-6" onSubmit={handleCommit}>
              <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-6">
                <PreviewCount label="CSV（出荷リスト）" value={batch.rowCount} />
                <PreviewCount label="新規登録" value={preview.registered_count} />
                <PreviewCount label="登録済みスキップ" value={preview.skipped_count} />
                <PreviewCount label="エラー" value={preview.error_count} />
                <PreviewCount label="送信予定メール" value={preview.notification_count} />
                <PreviewCount
                  label={unregisteredShipmentReferenceLabel}
                  value={unregisteredCount === undefined ? "—" : `${unregisteredCount} 件`}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                現在の未登録対象全体の参考値です。CSV の対象期間とは一致しない場合があります。
              </p>

              {preview.errors.length > 0 ? (
                <div className="grid gap-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h3 className="font-medium">エラー行</h3>
                      <p className="text-sm text-muted-foreground">
                        CSV を修正し、同じファイルをもう一度選択してください。
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadImportErrors(preview.errors)}
                    >
                      エラー CSV をダウンロード
                    </Button>
                  </div>
                  <div className="rounded-lg border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="pl-4">行番号</TableHead>
                          <TableHead>理由</TableHead>
                          <TableHead className="pr-4">コード</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {preview.errors.map((error, index) => (
                          <TableRow key={`${error.row_number}-${error.reason}-${index}`}>
                            <TableCell className="pl-4 tabular-nums">
                              {error.row_number}
                            </TableCell>
                            <TableCell>{importReason(error.reason)}</TableCell>
                            <TableCell className="pr-4 font-mono text-xs text-muted-foreground">
                              {error.reason}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ) : null}

              {importState.phase === "complete" ? (
                <Alert>
                  <AlertTitle>取込を確定しました</AlertTitle>
                  <AlertDescription>
                    新規 {importState.result.registered_count} 件、スキップ {importState.result.skipped_count} 件、発送通知メール {importState.result.notification_count} 件を送信キューへ登録しました。
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="grid gap-4 rounded-lg border p-4">
                  <label className="flex items-center gap-3 text-sm font-medium">
                    <input
                      type="checkbox"
                      className="size-4 accent-primary"
                      checked={recordHandover}
                      onChange={(event) => setRecordHandover(event.target.checked)}
                    />
                    郵便引渡し情報も記録する（任意）
                  </label>
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="grid gap-2">
                      <Label htmlFor="handed-over-on">引渡し日</Label>
                      <Input
                        id="handed-over-on"
                        type="date"
                        required={recordHandover}
                        value={handedOverOn}
                        onChange={(event) => setHandedOverOn(event.target.value)}
                        disabled={!recordHandover}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="handover-method">引渡し方法</Label>
                      <select
                        id="handover-method"
                        className="h-9 rounded-md border border-input bg-transparent px-3 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:opacity-50"
                        value={handoverMethod}
                        onChange={(event) =>
                          setHandoverMethod(
                            event.target.value as ShipmentHandover["method"],
                          )
                        }
                        disabled={!recordHandover}
                      >
                        <option value="counter">窓口持込</option>
                        <option value="pickup">集荷</option>
                      </select>
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="reported-count">報告件数</Label>
                      <Input
                        id="reported-count"
                        type="number"
                        min="0"
                        step="1"
                        required={recordHandover}
                        value={reportedCount}
                        onChange={(event) => setReportedCount(event.target.value)}
                        disabled={!recordHandover}
                      />
                    </div>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="handover-note">引渡しメモ（任意）</Label>
                    <Textarea
                      id="handover-note"
                      rows={2}
                      value={handoverNote}
                      onChange={(event) => setHandoverNote(event.target.value)}
                      disabled={!recordHandover}
                      placeholder="例: 集荷担当へ引渡し済み"
                    />
                  </div>

                  {commitError ? (
                    <Alert variant="destructive">
                      <AlertDescription>{commitError}</AlertDescription>
                    </Alert>
                  ) : null}

                  <div className="flex flex-wrap items-center justify-between gap-4 border-t pt-4">
                    <p className="font-medium">
                      発送通知メールが {preview.notification_count} 件送信されます
                    </p>
                    <Button
                      type="submit"
                      disabled={
                        !canCommitShipmentImport(importState) ||
                        !handoverValid ||
                        commitMutation.isPending
                      }
                    >
                      {commitMutation.isPending ? "確定中…" : "この内容で確定"}
                    </Button>
                  </div>
                  {preview.error_count > 0 ? (
                    <p className="text-sm text-destructive">
                      エラーがあるため確定できません。
                    </p>
                  ) : null}
                </div>
              )}
            </form>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>手入力で1件登録する</CardTitle>
          <CardDescription>
            少数出荷・交換品など、CSV を使わない登録に使用します。
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          <form className="grid items-end gap-4 lg:grid-cols-[12rem_1fr_18rem_auto]" onSubmit={handleManualReview}>
            <div className="grid gap-2">
              <Label htmlFor="manual-target-kind">対象種別</Label>
              <select
                id="manual-target-kind"
                className="h-9 rounded-md border border-input bg-transparent px-3 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
                value={manualKind}
                onChange={(event) =>
                  setManualKind(event.target.value as ShipmentTargetKind)
                }
              >
                <option value="cycle">定期の回</option>
                <option value="one_time_order">単品便</option>
              </select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="manual-target-id">対象キー</Label>
              <Input
                id="manual-target-id"
                required
                value={manualTargetId}
                onChange={(event) => setManualTargetId(event.target.value)}
                placeholder="UUID"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="manual-tracking-number">追跡番号</Label>
              <Input
                id="manual-tracking-number"
                required
                inputMode="numeric"
                value={manualTrackingNumber}
                onChange={(event) => setManualTrackingNumber(event.target.value)}
                placeholder="12桁（空白・ハイフン可）"
              />
            </div>
            <Button type="submit">登録内容を確認</Button>
          </form>

          {manualError ? (
            <Alert variant="destructive">
              <AlertDescription>{manualError}</AlertDescription>
            </Alert>
          ) : null}
          {manualResult?.result === "invalid" ? (
            <Alert variant="destructive">
              <AlertTitle>登録できませんでした</AlertTitle>
              <AlertDescription>
                {manualResult.errors.map((error) => importReason(error.reason)).join("、")}
              </AlertDescription>
            </Alert>
          ) : null}
        </CardContent>
      </Card>

      <section className="grid gap-4" aria-labelledby="pending-heading">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 id="pending-heading" className="text-xl font-semibold">
              3. 未処理一覧
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              未登録は取込当日、未引受は郵便引渡しの翌営業日に確認します。
            </p>
          </div>
          <div className="flex gap-2">
            <Badge variant={unregisteredCount ? "warning" : "secondary"}>
              未登録 {unregisteredCount ?? "—"}
            </Badge>
            <Badge
              variant={
                pendingQuery.data?.unaccepted.length ? "warning" : "secondary"
              }
            >
              未引受 {pendingQuery.data?.unaccepted.length ?? "—"}
            </Badge>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>未登録</CardTitle>
            <CardDescription>
              締切済みで shipment がない定期の回・単品便です。
            </CardDescription>
            <CardAction>
              <Badge variant={unregisteredCount ? "warning" : "secondary"}>
                {unregisteredCount ?? "—"} 件
              </Badge>
            </CardAction>
          </CardHeader>
          <CardContent className="px-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-6">お届け期間</TableHead>
                  <TableHead>対象</TableHead>
                  <TableHead>届け先</TableHead>
                  <TableHead>住所</TableHead>
                  <TableHead className="pr-6">品種 / 精米</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingQuery.isPending ? (
                  <EmptyRow columns={5} label="未登録を読み込んでいます…" />
                ) : pendingQuery.data?.unregistered.length ? (
                  pendingQuery.data.unregistered.map((shipment) => (
                    <TableRow key={`${shipment.target_kind}-${shipment.target_id}`}>
                      <TableCell className="pl-6">
                        {formatDate(shipment.delivery_window_start)}〜
                        {formatDate(shipment.delivery_window_end)}
                      </TableCell>
                      <TableCell>
                        <div>{targetKindLabel(shipment.target_kind)}</div>
                        <div className="font-mono text-xs text-muted-foreground" title={shipment.target_id}>
                          {shortId(shipment.target_id)}
                        </div>
                      </TableCell>
                      <TableCell>{shipment.recipient_name || "—"}</TableCell>
                      <TableCell>
                        〒{shipment.postal_code} {shipment.prefecture}
                        {shipment.address_line1}
                        {shipment.address_line2 ?? ""}
                      </TableCell>
                      <TableCell className="pr-6">
                        {formatVariety(shipment.variety)} / {formatMillingType(shipment.milling_type)}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <EmptyRow columns={5} label="未登録の出荷対象はありません。" />
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>未引受（日本郵便で受付未確認）</CardTitle>
            <CardDescription>
              追跡番号登録から1営業日を超えた発送です。追跡ページで確認した行を選択して記録します。
            </CardDescription>
            <CardAction>
              <Badge
                variant={
                  pendingQuery.data?.unaccepted.length ? "warning" : "secondary"
                }
              >
                {pendingQuery.data?.unaccepted.length ?? "—"} 件
              </Badge>
            </CardAction>
          </CardHeader>
          <CardContent className="grid gap-4 px-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12 pl-6">
                    <input
                      type="checkbox"
                      className="size-4 accent-primary"
                      aria-label="未引受をすべて選択"
                      checked={allUnacceptedSelected}
                      onChange={(event) => toggleAllShipments(event.target.checked)}
                      disabled={!pendingQuery.data?.unaccepted.length}
                    />
                  </TableHead>
                  <TableHead>追跡番号登録日時</TableHead>
                  <TableHead>対象</TableHead>
                  <TableHead>追跡番号</TableHead>
                  <TableHead className="pr-6">郵便引渡し記録</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingQuery.isPending ? (
                  <EmptyRow columns={5} label="未引受を読み込んでいます…" />
                ) : pendingQuery.data?.unaccepted.length ? (
                  pendingQuery.data.unaccepted.map((shipment) => (
                    <TableRow key={shipment.shipment_id}>
                      <TableCell className="pl-6">
                        <input
                          type="checkbox"
                          className="size-4 accent-primary"
                          aria-label={`追跡番号 ${shipment.tracking_number} を選択`}
                          checked={selectedShipmentIds.includes(shipment.shipment_id)}
                          onChange={(event) =>
                            toggleShipment(shipment.shipment_id, event.target.checked)
                          }
                        />
                      </TableCell>
                      <TableCell
                        {...dateDisplayProps(
                          shipment.tracking_number_registered_at,
                          formatDateTime,
                        )}
                      />
                      <TableCell>
                        <div>{targetKindLabel(shipment.target_kind)}</div>
                        <div className="font-mono text-xs text-muted-foreground" title={shipment.target_id}>
                          {shortId(shipment.target_id)}
                        </div>
                      </TableCell>
                      <TableCell className="font-mono">
                        {shipment.tracking_number}
                      </TableCell>
                      <TableCell className="pr-6">
                        <Badge
                          variant={
                            shipment.postal_handover_id ? "outline" : "warning"
                          }
                        >
                          {shipment.postal_handover_id ? "記録あり" : "記録なし"}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <EmptyRow columns={5} label="期限を超えた未引受はありません。" />
                )}
              </TableBody>
            </Table>

            <form
              className="flex flex-wrap items-end justify-end gap-3 border-t px-6 pt-4"
              onSubmit={(event) => {
                event.preventDefault();
                if (
                  selectedShipmentIds.length > 0 &&
                  acceptedOn &&
                  !acceptanceMutation.isPending
                ) {
                  acceptanceMutation.mutate();
                }
              }}
            >
              <div className="grid gap-2">
                <Label htmlFor="accepted-on">引受日</Label>
                <Input
                  id="accepted-on"
                  type="date"
                  required
                  value={acceptedOn}
                  onChange={(event) => setAcceptedOn(event.target.value)}
                />
              </div>
              <Button
                type="submit"
                disabled={
                  selectedShipmentIds.length === 0 ||
                  !acceptedOn ||
                  acceptanceMutation.isPending
                }
              >
                {acceptanceMutation.isPending
                  ? "記録中…"
                  : `選択した ${selectedShipmentIds.length} 件を一括記録`}
              </Button>
            </form>
            {acceptanceError ? (
              <Alert variant="destructive" className="mx-6">
                <AlertDescription>{acceptanceError}</AlertDescription>
              </Alert>
            ) : null}
          </CardContent>
        </Card>
      </section>

      <Dialog
        open={manualConfirmOpen}
        onOpenChange={(open) => {
          if (!open && !manualMutation.isPending) setManualConfirmOpen(false);
        }}
      >
        <DialogContent
          aria-labelledby="manual-shipment-dialog-title"
          aria-describedby="manual-shipment-dialog-description"
        >
          <form className="grid gap-6" onSubmit={handleManualCommit}>
            <DialogHeader>
              <DialogTitle id="manual-shipment-dialog-title">
                追跡番号を登録しますか？
              </DialogTitle>
              <DialogDescription id="manual-shipment-dialog-description">
                対象と追跡番号の組み合わせを確認してください。
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-3 px-6 text-sm">
              <div className="grid grid-cols-[7rem_1fr] gap-2">
                <span className="text-muted-foreground">対象</span>
                <span>
                  {targetKindLabel(manualKind)} / {manualTargetId.trim()}
                </span>
                <span className="text-muted-foreground">追跡番号</span>
                <span className="font-mono">{manualTrackingNumber.trim()}</span>
              </div>
              <Alert>
                <AlertDescription>
                  確定すると発送通知メールが送信されます。
                </AlertDescription>
              </Alert>
              {manualError ? (
                <Alert variant="destructive">
                  <AlertDescription>{manualError}</AlertDescription>
                </Alert>
              ) : null}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setManualConfirmOpen(false)}
                disabled={manualMutation.isPending}
              >
                キャンセル
              </Button>
              <Button type="submit" disabled={manualMutation.isPending}>
                {manualMutation.isPending ? "登録中…" : "登録を確定"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
